import './UserForm.scss';
import { Stack, Typography, Divider, Breadcrumbs, Link, Box, CircularProgress } from '@mui/material';
import UserManagementForm, { type UserForm } from './UserManagementForm.tsx';
import { useGetUserSummaryByAdminQuery } from '../../../api/user.api.ts';
import type { ExternalUserAccountInformationFormSummary } from '../../../api/models/user.model.ts';
import { useEffect, useState } from 'react';
import {
	USER_MANAGEMENT_DASHBOARD,
	EDIT_USER,
} from '../../../constants/messages.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { ColorBaseCharcoal, ColorBaseGreenEnergy } from '@huntington/honeycomb-design/dist';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alertSlice';

const initialUserForm: UserForm = {
	userType: 'external',
	firstName: '',
	lastName: '',
	phoneType: '',
	phoneNumber: '',
	emailAddress: '',
	externalUserAccounts: [{
		roleId: {label: '', value: ''},
		accountId: {label: '', value: ''},
	}]
}

function EditUser() {

	const navigate = useNavigate();
	const location = useLocation();
	const { userId, formState } = location.state ?? {};
	const dispatch = useDispatch();
	const [formattedUserData, setFormattedUserData] = useState<UserForm>(initialUserForm);

	const { data: userData, isError, isLoading, isFetching } = useGetUserSummaryByAdminQuery(
		{
			userId: userId,
		},
		{
			skip: !userId,
			refetchOnMountOrArgChange: true
		}
	);

	useEffect(() => {
		if (isError) {
			dispatch(showAlert({ message: 'Error loading user. Please try again later.', type: 'error' }));
			navigate('/user-management' );
		}
	}, [isError, dispatch, navigate])

	useEffect(() => {
		if (userData && !formState) {
			setFormattedUserData(prev => ({
				...prev,
				...userData,
				userType: 'external',
				externalUserAccounts: userData?.externalUserAccounts?.map((account: ExternalUserAccountInformationFormSummary) => {
					return {
						accountId: {
							label: account.accountName || '',
							value: String(account.accountId) || ''
						},
						roleId: { label: account.roleName, value: String(account.roleId) }
					};
				}) || [{
					roleId: {label: '', value: ''},
					accountId: {label: '', value: ''},
				}]
			}))
		}
	}, [userData, formState]);

	const triggerNavigation = async (payload: UserForm) => {
		navigate('/user-management/confirm-user', {
			state: {
				mode: 'edit',
				isDirty: true,
				userId,
				formState: {
					...payload,
					externalUserAccounts: payload.externalUserAccounts.map(account => ({
						accountId: account.accountId.value,
						accountLabel: account.accountId.label,
						roleId: account.roleId.value,
						roleLabel: account.roleId.label,
					}))
				}
			}
		});
	}

	if (isLoading || isFetching) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Stack spacing={3}>
			<Breadcrumbs aria-label="breadcrumb">
				<Link
					underline="always"
					color="inherit"
					onClick={() => navigate('/user-management')}
					sx={{cursor: 'pointer'}}
				>
					{USER_MANAGEMENT_DASHBOARD}
				</Link>
				<Typography sx={{color: 'text.primary', fontWeight: 'bold'}}>{EDIT_USER}</Typography>
			</Breadcrumbs>
			<Typography
				variant="h1"
				component="h1"
				sx={{
					color: ColorBaseGreenEnergy,
					fontWeight: 'bold',
					pl: '10px',
				}}
			>
				{USER_MANAGEMENT_DASHBOARD}
			</Typography>
			<Typography
				variant="subtitle1"
				component="h2"
				sx={{
					color: ColorBaseCharcoal,
					fontWeight: 'bold',
					pl: '10px',
				}}
			>
				{EDIT_USER}
			</Typography>
			<Divider/>
			<UserManagementForm userForm={formattedUserData} triggerNavigation={triggerNavigation}/>
		</Stack>
	);



}

export default EditUser;